import React, { useLayoutEffect, useRef, useState } from 'react';
import { Search, ChevronDown, SlidersHorizontal, ArrowUp, ArrowDown, X, Check } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import SectionHeader from './SectionHeader';
import clearOnEscape from './clearOnEscape';
import { useCloseOnBack } from './backLayer/useCloseOnBack';
import {
  computeOverflowLayout,
  SEARCH_MIN_WIDTH,
} from './filterStripOverflow';
import './menu.css';
import './FilterStrip.css';

/**
 * Baustein 3 — Filter-Strip.
 * Single-Row-Container für Filter und View-Modifier. Bricht NIE in eine
 * zweite Zeile um — stattdessen weichen Elemente automatisch in einen
 * einklappbaren „+ Filter"-Bereich unter der Zeile aus, sobald sie nicht
 * mehr passen (Details/Regelwerk: `docs/superpowers/specs/2026-07-25-filterstrip-overflow-design.md`).
 *
 * **Reihenfolge** (links → rechts, JSX-Reihenfolge = Wichtigkeit außen → innen):
 *   1. `FilterStrip.Search`
 *   2. `FilterStrip.Item`-gewrappte Filter-Groups (verengen die Sicht:
 *      Decks, Typen, Farbe, Label …) — weichen bei Platzmangel zuerst aus
 *      (innerste zuerst)
 *   3. Automatisch gerenderter `FilterStrip.More`-Knopf, sobald etwas
 *      ausgewichen ist ODER `activeExtraCount`/`FilterStrip.Extra` permanente
 *      Zusatzfilter mitbringen
 *   4. `FilterStrip.RightGroup` mit `FilterStrip.Item`-gewrappten
 *      View-Modifiern (Gruppieren, Sortierung + SortDirection)
 *
 * **Endstufe (Spec §6/V3-Festlegung 2):** Sind ALLE ausweichbaren Items (links
 * UND rechts) in den Filter-Knopf ausgewichen, füllt das Suchfeld die
 * verfügbare Breite und der Knopf rückt an den rechten Rand. Strips ohne
 * Items (nur Suche + permanente Extras) bleiben unverändert — die Endstufe
 * gilt nur, wenn tatsächlich etwas ausgewichen ist. Fixe Nicht-Item-Kinder der
 * RightGroup (z. B. ein Zahnrad-Popover) weichen nie aus und stehen in der
 * Endstufe weiterhin rechts vom „+ Filter"-Knopf — „rechtsbündig" bezieht sich
 * nur auf die ausweichbaren Elemente.
 *
 * **Item-Marker:** Jedes ausweichbare Zeilen-Element wird in
 * `<FilterStrip.Item id="…" active?>` gewrappt (ein Item = eine
 * `Group`/`ManaColors`). Nicht gewrappte Kinder (Suche, ein Zahnrad in
 * `RightGroup`) sind fix und weichen nie aus. Der Strip misst alle Items
 * unsichtbar in einer zweiten Instanz (`.filter-strip__measure`) und
 * berechnet daraus per `computeOverflowLayout`, welche Items in
 * die Zeile passen — mit Hysterese gegen Flattern an der Kippkante.
 *
 * **`activeExtraCount` + `FilterStrip.Extra`:** `FilterStrip.Extra` wird
 * IMMER unbedingt als Kind übergeben (nicht mehr conditional als Sibling) —
 * der Strip liest sie nur als Daten-Slot und rendert den Bereich selbst.
 * `activeExtraCount` zählt die aktiven PERMANENTEN Zusatzfilter darin; der
 * Strip addiert selbst ausgewichene aktive Items für die Knopf-Zahl dazu.
 *
 * **Label-Konvention:** Jeder Pill steht in einer `Group` mit dem Label.
 * Niemals Label direkt auf den Pill schreiben — das würde zu zwei Wegen
 * für dieselbe Sache führen und die Border-Left-Separatoren zwischen
 * Groups brechen. Passt selbst mit Label nichts mehr in die Zeile, blendet
 * die Logik zuerst nur die Labels aus (`filter-strip--no-labels`), bevor
 * sie Items ausweichen lässt — im Bereich sind Labels immer sichtbar.
 *
 * **Mobile-Dock (≤900px, Spec §6/V3-Festlegung 1):** Der Wrap trägt per
 * Default die Klasse `filter-strip-wrap--dock`; ab 900px Viewport-Breite
 * schwebt die Leiste dadurch über dem Inhalt — mit Abstand zu allen drei
 * Rändern (`--filter-strip-dock-gap`), gerundet und beschattet; der
 * „+ Filter"-Bereich öffnet nach oben (`FilterStrip.css`). Alle Bedienelemente
 * wachsen dort auf 44px Touch-Höhe und die Suche bekommt eine größere
 * Mindestbreite, damit Gruppen wie die Sortierung früher in den Filter-Knopf
 * ausweichen statt das Feld zu stauchen — beide Werte stehen in
 * `FilterStrip.css`, die Mindestbreite liest die Overflow-Rechnung von dort ab.
 * Höhe der Zeile im Dock-Zustand ist der Token `--filter-strip-dock-height`,
 * ihr Randabstand `--filter-strip-dock-gap`; auf beiden zusammen bauen vier
 * `:has()`-Verträge auf: `Page.css` gibt Seiten mit schwebender
 * Leiste automatisch unteren Inhaltsabstand, `DetailSidePanel.css` rückt das
 * mobile Wantlist-Sheet über die Leiste, `Toast.css` rückt den Toast-Viewport
 * darüber, `App.css` den Scroll-to-top-Knopf. Wer sonst noch unter der Leiste
 * liegt, rechnet beide Token mit —
 * `EditorChrome.css` (Hinzufügen-Feld) und `MobileDeckEditorPage.js`
 * (Sichtbarkeitsprüfung beim Springen zu einer Karte) tun das. Die Prop `mobileDock={false}` schaltet den Dock-Zustand ab.
 * Zwei legitime Fälle: Showcase-/Demo-Kontexte mit mehreren gleichzeitig
 * sichtbaren Strips — und eine Leiste, die IN einer eingebetteten Fläche sitzt
 * (Panel, Seitenspalte, eigenes Fenster; z.B. der Karten-Katalog des
 * Deck-Editors): dort filtert die Leiste nur den Inhalt ihrer Fläche, im
 * Dock-Zustand löste sie sich aus dem Panel und legte sich über die ganze
 * Seite. Ein Strip, der die Seite filtert, lässt den Default stehen.
 * Am Handy (≤900px) schließt auch die Zurück-Geste den aufgeklappten
 * „+ Filter"-Bereich.
 *
 * **`FilterStrip.DockBar`:** zweite Dock-Zeile für Konsumenten mit einer
 * eigenen mobilen Werkzeugleiste (z.B. der mobile Editor). Als Kind von
 * `<FilterStrip>` übergeben, rendert sie NUR im Dock-Zustand (≤900px) —
 * außerhalb des Dock-Modus bleibt sie unsichtbar, der Konsument braucht
 * dafür keine eigene Sichtbarkeits-Logik. Im Dock erscheint sie DIREKT ÜBER
 * der Filterzeile (`column-reverse`: Zeile unten, DockBar darüber, „+
 * Filter"-Bereich zuoberst, wenn geöffnet). Nur wenn zusätzlich der
 * Dock-Zustand aktiv ist (`mobileDock` nicht auf `false` gesetzt), trägt der
 * Wrap die Klasse `filter-strip-wrap--has-dock-bar`, worauf vier
 * `:has()`-Verträge `+ var(--filter-strip-dock-bar-height)` aufaddieren
 * (dieselben vier: `Page.css`, `DetailSidePanel.css`, `Toast.css`, `App.css`),
 * damit Inhalt, Toasts, Sheets und der Knopf über BEIDEN Zeilen bleiben. Höhe ist der eigene Token
 * `--filter-strip-dock-bar-height`; Bedienelemente darin binden ihre Höhe
 * an `--filter-strip-control-height` (liegt auf dem Wrap, im Dock 44px) —
 * dieselbe Touch-Ziel-Quelle wie Suchfeld und Pills, keine eigene Höhe
 * erfinden.
 *
 * Komposition:
 *   <FilterStrip activeExtraCount={aktiveZusatzfilter}>
 *     <FilterStrip.Search ... />
 *     <FilterStrip.Item id="typen" active={typenAktiv}>
 *       <FilterStrip.Group label="Typen">
 *         <FilterStrip.Pill value="Alle" />
 *       </FilterStrip.Group>
 *     </FilterStrip.Item>
 *     <FilterStrip.RightGroup>
 *       <FilterStrip.Item id="sortierung">
 *         <FilterStrip.Group label="Sortierung">
 *           <FilterStrip.Pill value="Name" />
 *           <FilterStrip.SortDirection direction="desc" />
 *         </FilterStrip.Group>
 *       </FilterStrip.Item>
 *     </FilterStrip.RightGroup>
 *     <FilterStrip.Extra>
 *       <FilterStrip.Group label="Regeltext">
 *         <FilterStrip.TextInput value="" onChange={() => {}} />
 *       </FilterStrip.Group>
 *     </FilterStrip.Extra>
 *   </FilterStrip>
 */

/**
 * Marker: ausweichbares Zeilen-Element. Rendert seine Children unverändert.
 *
 * **Achtung Doppel-Instanzen:** Der Strip rendert die `children` eines Item
 * (und die nicht-Item-Kinder eines `RightGroup`, s.u.) IMMER zweimal — einmal
 * sichtbar in der Zeile/im Bereich, einmal unsichtbar in der Mess-Zeile.
 * Item-Kinder müssen deshalb **kontrolliert** (Wert kommt per Prop von außen,
 * kein interner uncontrolled State), **ref-frei** (kein `ref` auf ein
 * DOM-Element oder Kind-Component-Instanz — ein `ref` würde auf die
 * zufällig zuletzt gemountete Instanz zeigen) und **frei von Seiteneffekten**
 * (kein Analytics-Ping, kein Subscribe in einem Kind-`useEffect`, das beim
 * zweiten Mount unerwünscht doppelt liefe) sein.
 */
const Item = function FilterStripItem({ children }) { return children; };

export default function FilterStrip({ children, activeExtraCount = 0, mobileDock = true }) {
  // ── Introspektion: Kinder klassifizieren (keine Hooks — Reihenfolge egal) ──
  const kids = React.Children.toArray(children).filter(Boolean);
  const isType = (el, t) => React.isValidElement(el) && el.type === t;
  const search = kids.find((k) => isType(k, FilterStrip.Search)) || null;
  const extra = kids.find((k) => isType(k, FilterStrip.Extra)) || null;
  const dockBarEl = kids.find((k) => isType(k, FilterStrip.DockBar)) || null;
  const rightGroupEl = kids.find((k) => isType(k, FilterStrip.RightGroup)) || null;
  const leftItems = kids.filter((k) => isType(k, Item));
  const leftFixed = kids.filter(
    (k) => k !== search && k !== extra && k !== dockBarEl && k !== rightGroupEl && !isType(k, Item)
  );
  const rightKids = rightGroupEl
    ? React.Children.toArray(rightGroupEl.props.children).filter(Boolean)
    : [];
  const rightItems = rightKids.filter((k) => isType(k, Item));
  const rightFixed = rightKids.filter((k) => !isType(k, Item));

  const rowRef = useRef(null);
  const measureRef = useRef(null);
  // Initialer `showMore` kommt schon aus den Props (`extra` braucht keine
  // Messung) — wichtig für den Fall, dass die Messung nie greift (jsdom
  // ohne ResizeObserver, containerWidth bleibt 0 → Guard unten bricht ab
  // und rührt den State nie an): sonst bliebe der „+ Filter"-Knopf für
  // permanente Extras für immer unsichtbar, obwohl nichts gemessen werden
  // musste, um seine Existenz zu kennen. Lazy Initializer, weil er nur
  // beim allerersten Render zählt — danach übernimmt die echte Messung.
  const [layout, setLayout] = useState(() => ({
    evictedIds: [], labelsHidden: false, moreCompact: false, showMore: Boolean(extra),
  }));
  const [extraOpen, setExtraOpen] = useState(false);
  const prevEvictedRef = useRef([]);

  // ── Messen + Rechnen (vor dem ersten Paint — kein Aufblitzen) ──
  useLayoutEffect(() => {
    const rowEl = rowRef.current;
    const measureEl = measureRef.current;
    if (!rowEl || !measureEl) return undefined;

    const recompute = () => {
      const style = getComputedStyle(rowEl);
      const gap = parseFloat(style.columnGap) || 0;
      const containerWidth =
        rowEl.clientWidth -
        (parseFloat(style.paddingLeft) || 0) -
        (parseFloat(style.paddingRight) || 0);
      // Nicht messbar (jsdom, display:none, noch 0 breit): Zustand nicht anfassen —
      // sonst würde eine 0-Breite alles ausweichen lassen.
      if (containerWidth <= 0) return;
      const readItems = (side) =>
        Array.from(measureEl.querySelectorAll(`[data-fs-side="${side}"]`)).map((el) => ({
          id: el.dataset.fsId,
          width: el.offsetWidth,
          labelWidth: el.querySelector('.filter-strip__group-label')?.offsetWidth || 0,
        }));
      // Mindestbreite der Suche kommt aus dem CSS: im Dock-Zustand ist sie
      // größer, damit Gruppen früher ausweichen statt das Feld zu stauchen.
      // Ausgelesen statt hier hinterlegt, damit der 900px-Breakpoint allein in
      // FilterStrip.css steht. Fallback deckt Umgebungen ohne angewandtes CSS ab.
      const searchEl = search ? rowEl.querySelector('.filter-strip__field--search') : null;
      const searchMinWidth = searchEl
        ? parseFloat(getComputedStyle(searchEl).minWidth) || SEARCH_MIN_WIDTH
        : 0;
      const fixedEls = Array.from(measureEl.querySelectorAll('[data-fs-side="fixed"]'));
      const fixedWidth = fixedEls.reduce((s, el) => s + el.offsetWidth, 0);
      const moreFullWidth =
        measureEl.querySelector('[data-fs-measure="more-full"]')?.offsetWidth || 0;
      const moreCompactWidth =
        measureEl.querySelector('[data-fs-measure="more-compact"]')?.offsetWidth || 0;

      const next = computeOverflowLayout({
        containerWidth, gap,
        leftItems: readItems('left'),
        rightItems: readItems('right'),
        fixedWidth,
        searchMinWidth,
        moreFullWidth, moreCompactWidth,
        hasPermanentExtras: Boolean(extra),
        previousEvictedIds: prevEvictedRef.current,
      });
      prevEvictedRef.current = next.evictedIds;
      setLayout((cur) =>
        cur.labelsHidden === next.labelsHidden &&
        cur.moreCompact === next.moreCompact &&
        cur.showMore === next.showMore &&
        cur.evictedIds.join('|') === next.evictedIds.join('|')
          ? cur
          : next
      );
    };

    recompute();
    if (typeof ResizeObserver === 'undefined') return undefined; // jsdom
    const ro = new ResizeObserver(recompute);
    ro.observe(rowEl);
    Array.from(measureEl.children).forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  });
  // Kein Dependency-Array: Kinder (Pill-Texte) ändern sich mit jedem Render;
  // die Mess-Elemente werden dann neu observiert. setLayout ist identitäts-stabil
  // (Bail-out oben), daher keine Endlos-Schleife.

  const evicted = new Set(layout.evictedIds);
  const visibleLeft = leftItems.filter((it) => !evicted.has(it.props.id));
  const evictedLeft = leftItems.filter((it) => evicted.has(it.props.id));
  const visibleRight = rightItems.filter((it) => !evicted.has(it.props.id));
  const evictedRight = rightItems.filter((it) => evicted.has(it.props.id));
  const activeEvicted = [...evictedLeft, ...evictedRight].filter((it) => it.props.active).length;
  const moreCount = activeExtraCount + activeEvicted;
  const showPanel = extraOpen && layout.showMore;
  useCloseOnBack(showPanel, () => setExtraOpen(false));

  // Endstufe des Abbaus: es GAB ausweichbare Items und keines steht mehr in der
  // Zeile → Suchfeld füllt die Breite (CSS), der More-Knopf rückt an den Rand.
  // Strips ohne Items (nur Suche + permanente Extras) behalten ihr Layout.
  const searchOnly =
    Boolean(search) &&
    leftItems.length + rightItems.length > 0 &&
    visibleLeft.length === 0 &&
    visibleRight.length === 0;

  return (
    <div className={`filter-strip-wrap${mobileDock ? ' filter-strip-wrap--dock' : ''}${dockBarEl && mobileDock ? ' filter-strip-wrap--has-dock-bar' : ''}`}>
      <div
        ref={rowRef}
        className={`filter-strip${layout.labelsHidden ? ' filter-strip--no-labels' : ''}${searchOnly ? ' filter-strip--search-only' : ''}`}
      >
        {search}
        {leftFixed}
        {visibleLeft}
        {layout.showMore && (
          <FilterStrip.More
            count={moreCount}
            expanded={extraOpen}
            compact={layout.moreCompact}
            onClick={() => setExtraOpen((v) => !v)}
          />
        )}
        {rightGroupEl &&
          React.cloneElement(rightGroupEl, {}, [...visibleRight, ...rightFixed])}
      </div>

      {/* Mess-Zeile: alle Items mit Labels, plus beide Knopf-Varianten */}
      <div ref={measureRef} className="filter-strip__measure" aria-hidden="true">
        {leftItems.map((it) => (
          <div key={it.props.id} className="filter-strip__measure-item" data-fs-side="left" data-fs-id={it.props.id}>
            {it.props.children}
          </div>
        ))}
        {rightItems.map((it) => (
          <div key={it.props.id} className="filter-strip__measure-item" data-fs-side="right" data-fs-id={it.props.id}>
            {it.props.children}
          </div>
        ))}
        {rightFixed.map((el, i) => (
          <div key={`fixed-${i}`} className="filter-strip__measure-item" data-fs-side="fixed">
            {el}
          </div>
        ))}
        <div className="filter-strip__measure-item" data-fs-measure="more-full">
          <FilterStrip.More count={moreCount} />
        </div>
        <div className="filter-strip__measure-item" data-fs-measure="more-compact">
          <FilterStrip.More count={moreCount} compact />
        </div>
      </div>

      {dockBarEl}

      {showPanel && (
        <div className="filter-strip__extra">
          <div className="filter-strip__extra-row">
            {evictedLeft.map((it) => <React.Fragment key={it.props.id}>{it.props.children}</React.Fragment>)}
            {extra?.props.children}
          </div>
          {evictedRight.length > 0 && (
            <>
              <SectionHeader size="sm">Ansicht</SectionHeader>
              <div className="filter-strip__extra-row">
                {evictedRight.map((it) => <React.Fragment key={it.props.id}>{it.props.children}</React.Fragment>)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
FilterStrip.Item = Item;

/**
 * Suchfeld — gehört immer ganz links.
 * Optional `help`-Slot rechts im Feld (z.B. `<SyntaxHelpPopover />`).
 * Zeigt einen Clear-X-Button, sobald `value` nicht leer ist; Escape leert ebenso.
 * Fokus-Ring liegt per `:focus-within` auf dem Feld-Rahmen; das Input trägt `filter-strip__input` als Ausnahme von der globalen Übergangsregel in `App.css`.
 */
FilterStrip.Search = function FilterStripSearch({ value, onChange, placeholder = 'Suche…', help }) {
  return (
    <div className="filter-strip__field filter-strip__field--search">
      <Search size={16} aria-hidden className="filter-strip__field-icon" />
      <input
        type="text"
        className="filter-strip__input"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        {...clearOnEscape({ hasValue: Boolean(value), clear: () => onChange?.('') })}
        placeholder={placeholder}
      />
      {value && (
        <button
          type="button"
          className="filter-strip__clear"
          onClick={() => onChange?.('')}
          aria-label="Suche löschen"
        >
          <X size={14} aria-hidden />
        </button>
      )}
      {help}
    </div>
  );
};

/**
 * TextInput — sekundäres Text-Eingabefeld innerhalb des Strips.
 * Gleiche Höhe + Border wie `FilterStrip.Search`, aber ohne Lupe-Icon —
 * gehört in eine `FilterStrip.Group` mit Label (z.B. „Regeltext"),
 * weil die fehlende Lupe sonst die Bedeutung des Felds unklar macht.
 * Zeigt denselben Clear-X-Button wie Search, inklusive Leeren per Escape.
 * Fokus-Ring liegt per `:focus-within` auf dem Feld-Rahmen; das Input trägt `filter-strip__input` als Ausnahme von der globalen Übergangsregel in `App.css`.
 */
FilterStrip.TextInput = function FilterStripTextInput({ value, onChange, placeholder = '', width = 180 }) {
  return (
    <div className="filter-strip__field filter-strip__field--text" style={{ width: `${width}px` }}>
      <input
        type="text"
        className="filter-strip__input"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        {...clearOnEscape({ hasValue: Boolean(value), clear: () => onChange?.('') })}
        placeholder={placeholder}
      />
      {value && (
        <button
          type="button"
          className="filter-strip__clear"
          onClick={() => onChange?.('')}
          aria-label="Eingabe löschen"
        >
          <X size={14} aria-hidden />
        </button>
      )}
    </div>
  );
};

/**
 * Group — kapselt zusammengehörige Filter mit gemeinsamem Label.
 * Label-Konvention: Jede Group hat ein Label. Das Control daneben zeigt
 * den aktuellen Zustand (Inline-Optionen direkt sichtbar / Pill mit value).
 */
FilterStrip.Group = function FilterStripGroup({ label, children }) {
  return (
    <div className="filter-strip__group">
      {label && <span className="filter-strip__group-label">{label}</span>}
      <div className="filter-strip__group-items">{children}</div>
    </div>
  );
};

/**
 * Pill — Outlined-Chip-Button (Dropdown-Trigger).
 * Steht immer in einer `FilterStrip.Group` — das Label gehört in den Group-Wrapper.
 *
 * Props:
 *   value   — aktueller Filterwert (z.B. „Alle", „3 ausgewählt")
 *   active  — boolean, ob der Filter gesetzt ist (Accent-Border)
 *   onClick — Click-Handler
 */
FilterStrip.Pill = function FilterStripPill({ value, active = false, onClick }) {
  return (
    <button
      type="button"
      className={`filter-strip__pill${active ? ' filter-strip__pill--active' : ''}`}
      onClick={onClick}
    >
      {value && <span className="filter-strip__pill-value">{value}</span>}
      <ChevronDown size={14} aria-hidden className="filter-strip__pill-caret" />
    </button>
  );
};

/**
 * More — „+ Filter"-Button mit dashed-Border (collapsed) / solid-accent (expanded).
 * Zeigt optional einen Count, wenn expanded filters Werte haben.
 *
 * `compact` (nur noch master-intern gerendert, wenn selbst „Filter (n)" nicht
 * mehr in die Zeile passt): nur Icon + nackte Zahl, kein Wort, keine Klammern.
 * `count === 0` im Compact-Modus → nur das Icon (leerer `<span>`).
 */
FilterStrip.More = function FilterStripMore({ count = 0, expanded = false, onClick, label = 'Filter', compact = false }) {
  const isActive = expanded || count > 0;
  // Compact-Variante zeigt nur Icon + nackte Zahl (kein Wort) — ohne aria-label
  // hätte der Button dann keinen zugänglichen Namen für Screenreader.
  const accessibleName = count > 0 ? `${label} (${count})` : label;
  return (
    <button
      type="button"
      className={`filter-strip__more${isActive ? ' filter-strip__more--active' : ''}${compact ? ' filter-strip__more--compact' : ''}`}
      onClick={onClick}
      aria-expanded={expanded}
      aria-label={accessibleName}
    >
      <SlidersHorizontal size={14} aria-hidden />
      <span>{compact ? (count > 0 ? count : '') : `${label}${count > 0 ? ` (${count})` : ''}`}</span>
    </button>
  );
};

/**
 * RightGroup — View-Modifier rechtsbündig (margin-left: auto).
 *
 * Nicht-`Item`-Kinder darin (z.B. ein Zahnrad-Popover) gelten als fix und
 * weichen nie aus — sie werden dafür genau wie `Item`-Kinder zweimal
 * gerendert (sichtbar + in der unsichtbaren Mess-Zeile für `fixedWidth`).
 * Es gilt dieselbe Doppel-Instanz-Regel wie bei `FilterStrip.Item`:
 * kontrolliert, ref-frei, keine Seiteneffekte im Kind.
 */
FilterStrip.RightGroup = function FilterStripRightGroup({ children }) {
  return <div className="filter-strip__right">{children}</div>;
};

/**
 * Extra — benannter Daten-Slot für permanente Zusatzfilter, die unter dem
 * Haupt-Strip erscheinen, wenn der Nutzer auf `FilterStrip.More` klickt.
 * Wird UNBEDINGT als Kind von `<FilterStrip>` übergeben — der Strip liest
 * nur `props.children` und rendert den kompletten `.filter-strip__extra`-
 * Bereich (inkl. ausgewichener Items) selbst; das eigene Markup dieser
 * Komponente wird in diesem Fall NICHT gerendert (keine doppelte Box).
 * Children sind typisch mehrere `FilterStrip.Group`-Blöcke mit
 * `FilterStrip.TextInput` darin — die Group-Trennlinien sind hier per CSS
 * deaktiviert, weil die Felder eine zusammenhängende sekundäre Filter-Reihe
 * bilden.
 *
 * Rückwärtskompatibilität: für noch nicht migrierte Konsumenten, die
 * `<FilterStrip.Extra>` als eigenständiges Element AUSSERHALB von
 * `<FilterStrip>` rendern (alte API, conditional sibling), rendert die
 * Komponente weiterhin ihr eigenes Markup — sie wird dort nicht vom Strip
 * eingesammelt und muss sich also selbst darstellen.
 */
FilterStrip.Extra = function FilterStripExtra({ children }) {
  return <div className="filter-strip__extra">{children}</div>;
};

/**
 * DockBar — zweite Dock-Zeile für eine konsumenten-eigene mobile Werkzeugleiste
 * (z.B. der mobile Editor). Als Kind von `<FilterStrip>` übergeben; der Root
 * extrahiert sie am Typ (wie `FilterStrip.Extra`) und rendert sie DIREKT ÜBER
 * der Filterzeile — außerhalb des Dock-Zustands (>900px) bleibt sie per CSS
 * unsichtbar (`display: none`), unabhängig davon, ob sie als Kind übergeben
 * wurde. Nimmt NICHT an der Overflow-Messung teil (kein zweites Mount in
 * `.filter-strip__measure`) — Kinder dürfen deshalb Seiteneffekte haben,
 * anders als `FilterStrip.Item`/`RightGroup`-Fixed-Kinder.
 */
FilterStrip.DockBar = function FilterStripDockBar({ children }) {
  return <div className="filter-strip__dock-bar">{children}</div>;
};

/**
 * ManaColors — Inline-Row aus klickbaren Mana-Icons (W/U/B/R/G/C).
 * Steht direkt im FilterStrip (kein Dropdown), weil Mana-Farben das
 * ikonischste MTG-Filter-Pattern sind und Scan-Zeit bei einem Klick
 * teurer wäre. Konsumenten geben die Icon-Quelle pro Farbe vor.
 *
 * Props:
 *   colors    — [{ code, name, iconSrc }] z.B. [{ code:'W', name:'Weiß', iconSrc:ManaW }]
 *   selected  — Array von codes, die als aktiv markiert sind
 *   onToggle  — (code) => void
 *   label     — optionales Group-Label (default „Farbe")
 */
FilterStrip.ManaColors = function FilterStripManaColors({ colors = [], selected = [], onToggle, label = 'Farbe' }) {
  return (
    <div className="filter-strip__group">
      {label && <span className="filter-strip__group-label">{label}</span>}
      <div className="filter-strip__group-items filter-strip__mana-row">
        {colors.map((c) => {
          const isActive = selected.includes(c.code);
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => onToggle?.(c.code)}
              className={`filter-strip__mana${isActive ? ' filter-strip__mana--active' : ''}`}
              title={c.name}
              aria-label={`Mana ${c.name}`}
              aria-pressed={isActive}
            >
              <img src={c.iconSrc} alt={c.code} className="filter-strip__mana-icon" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** SortDirection — kleiner Toggle für auf-/absteigend mit Lucide-Icon */
FilterStrip.SortDirection = function FilterStripSortDirection({ direction = 'desc', onClick }) {
  const Icon = direction === 'desc' ? ArrowDown : ArrowUp;
  return (
    <button
      type="button"
      className="btn-icon filter-strip__sort-dir"
      onClick={onClick}
      aria-label={direction === 'desc' ? 'Absteigend, umschalten auf aufsteigend' : 'Aufsteigend, umschalten auf absteigend'}
    >
      <Icon size={16} aria-hidden />
    </button>
  );
};

/**
 * PillMenu — `Pill` als Trigger + Radix-Dropdown mit Optionen.
 * mode='single' → RadioGroup (schließt bei Auswahl); mode='multi' → CheckboxItems
 * (bleibt offen). Steht wie `Pill` immer in einer `FilterStrip.Group`.
 *
 * Props:
 *   mode        — 'single' | 'multi'
 *   options     — [{ value, label, icon? }] — icon: optionale Icon-Komponente (lucide),
 *                 wird im Menü vor dem Label gezeigt
 *   value       — string (single) | string[] (multi)
 *   onChange    — single: (value) => void ; multi: (nextArray) => void
 *   placeholder — Pill-Text wenn nichts Sinnvolles anzuzeigen ist (multi: bei 0 Auswahl)
 *   allLabel    — optional (multi): zeigt diesen Text statt Zähler bei Vollauswahl
 *   active      — optional Override für den Aktiv-Zustand (Accent-Border). Default:
 *                 multi = (Auswahl > 0); single = false (Single-Selects wie Sortierung/
 *                 Quelle halten immer einen Wert — der Konsument setzt `active` explizit
 *                 bei einem bedeutsamen Nicht-Default, z.B. active={source !== 'all'}).
 *   onClear     — optional (multi): zeigt „Auswahl löschen"-Footer
 */
FilterStrip.PillMenu = function FilterStripPillMenu({
  mode = 'single',
  options = [],
  value,
  onChange,
  placeholder = 'Wählen',
  allLabel,
  active,
  onClear,
}) {
  const isMulti = mode === 'multi';
  const selected = isMulti ? (value || []) : value;

  const isAllSelected = isMulti && allLabel && options.length > 0 && selected.length === options.length;
  const pillValue = isMulti
    ? (selected.length > 0
        ? (isAllSelected ? allLabel : `${selected.length} ausgewählt`)
        : placeholder)
    : (options.find((o) => o.value === selected)?.label || placeholder);

  const isActive = active != null ? active : (isMulti ? selected.length > 0 : false);

  const toggleMulti = (optValue) => {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange?.(next);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={`filter-strip__pill${isActive ? ' filter-strip__pill--active' : ''}`}
        >
          <span className="filter-strip__pill-value">{pillValue}</span>
          <ChevronDown size={14} aria-hidden className="filter-strip__pill-caret" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="ui-menu" sideOffset={4} align="start">
          {isMulti
            ? options.map((opt) => (
                <DropdownMenu.CheckboxItem
                  key={opt.value}
                  className="ui-menu__item"
                  checked={selected.includes(opt.value)}
                  onCheckedChange={() => toggleMulti(opt.value)}
                  onSelect={(e) => e.preventDefault()}
                >
                  <span className="ui-menu__check">
                    <DropdownMenu.ItemIndicator>
                      <Check size={14} aria-hidden />
                    </DropdownMenu.ItemIndicator>
                  </span>
                  {opt.icon && <opt.icon size={14} aria-hidden className="ui-menu__icon" />}
                  {opt.label}
                </DropdownMenu.CheckboxItem>
              ))
            : (
              <DropdownMenu.RadioGroup value={selected} onValueChange={(v) => onChange?.(v)}>
                {options.map((opt) => (
                  <DropdownMenu.RadioItem
                    key={opt.value}
                    className="ui-menu__item"
                    value={opt.value}
                  >
                    <span className="ui-menu__check">
                      <DropdownMenu.ItemIndicator>
                        <Check size={14} aria-hidden />
                      </DropdownMenu.ItemIndicator>
                    </span>
                    {opt.icon && <opt.icon size={14} aria-hidden className="ui-menu__icon" />}
                    {opt.label}
                  </DropdownMenu.RadioItem>
                ))}
              </DropdownMenu.RadioGroup>
            )}
          {isMulti && onClear && selected.length > 0 && (
            <>
              <DropdownMenu.Separator className="ui-menu__sep" />
              <DropdownMenu.Item
                className="ui-menu__item ui-menu__item--action"
                onSelect={onClear}
              >
                Auswahl löschen
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
